import { Col, Row } from "antd";
import classNames from "classnames";
import _ from "lodash";
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ADDON_MODAL_CONSTANTS,
  BUNDLE_ADDONS_FILTER_TYPE,
  MAX_BUNDLE_SIZE
} from "rivals/components/shared/AddonOptions/constants";
import RivalsButton from "rivals/components/shared/Button";
import CustomButton from "rivals/components/shared/CustomButton";
import FormInput from "rivals/components/shared/Form/TextInput";
import { InputType } from "rivals/components/shared/Form/TextInput/types";
import { SearchIcon } from "rivals/public/static/icons/";
import {
  addAddOnToBundle,
  removeAddOnFromBundle
} from "rivals/redux/subscriptionAddons/actions";
import { getBundle } from "rivals/redux/subscriptionAddons/selectors";
import { ENTER_KEY_CODE } from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { setData } from "rivals/shared/utils/qa";
import styles from "./index.module.scss";
import strings from "./strings";
import { BundleAddOnsProps } from "./types";

const {
  addButton,
  bundleAddOnsStepText1,
  bundleAddOnsStepTitle,
  cancelButton,
  nextButton,
  noResults,
  removeButton,
  searchAnotherSchool,
  searchForYourRivals
} = strings;

const BundleAddOnsStep = ({
  addOns,
  closeAndReset,
  onMoveToNextStep,
  parentPhilter
}: BundleAddOnsProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [showSearchOverlay, setShowSearchOverlay] = useState<boolean>(true);
  const dispatch = useDispatch();
  const bundle = useSelector(getBundle);
  const searchAddOnLayoutRef = useRef<HTMLDivElement>(null);

  const nonPurchasedSpyOnlyAddOns =
    addOns &&
    addOns.length &&
    _.uniqBy(
      addOns.filter(
        addOn =>
          addOn.type === ADDON_MODAL_CONSTANTS.SPY &&
          addOn.associatedSubscription &&
          !addOn.associatedSubscription.subscribed
      ),
      "id"
    );

  const onDocumentClick = (e: MouseEvent): void => {
    const target = e.target as Element;
    if (searchAddOnLayoutRef && searchAddOnLayoutRef.current) {
      if (!searchAddOnLayoutRef.current.contains(target)) {
        if (showSearchOverlay) {
          setShowSearchOverlay(false);
        }
      }
    }
  };

  useEffect(() => {
    if (process.browser) {
      window.document.addEventListener("click", onDocumentClick, false);
      return (): void => {
        window.document.removeEventListener("click", onDocumentClick);
      };
    }
  }, [showSearchOverlay]);

  const onSearchForAddOns = (e: ChangeEvent<HTMLInputElement>): void => {
    setShowSearchOverlay(true);
    setSearchValue(e.target.value);
  };

  const onRemoveAddOnFromBundle = (id: number): void => {
    dispatch(removeAddOnFromBundle(id));
  };

  const onAddAddOnToBundle = (addon: AddOn): void => {
    if (bundle && bundle.length < MAX_BUNDLE_SIZE) {
      dispatch(addAddOnToBundle(addon));
      setSearchValue("");
    }
  };

  const filteredAddons =
    nonPurchasedSpyOnlyAddOns &&
    bundle &&
    _.difference(nonPurchasedSpyOnlyAddOns, bundle).filter(
      item =>
        item[BUNDLE_ADDONS_FILTER_TYPE].toLowerCase().includes(
          searchValue.toLowerCase()
        ) || item[BUNDLE_ADDONS_FILTER_TYPE] === ""
    );

  const onEnterPress = (e: KeyboardEvent): void => {
    if (e.which === ENTER_KEY_CODE && !!searchValue) {
      if (filteredAddons && filteredAddons.length) {
        if (bundle && bundle.length < MAX_BUNDLE_SIZE) {
          dispatch(addAddOnToBundle(filteredAddons[0]));
          setSearchValue("");
        }
      }
    }
  };

  const clearSearchField = (): void => setSearchValue("");

  const onCancel = (): void => {
    closeAndReset(false, clearSearchField);
  };

  return (
    <div
      className={styles.modalBody}
      {...setData(parentPhilter, "bundle-step")}
    >
      <div className={styles.addOnTitleWrapper}>
        <CustomButton
          ariaLabel="go-back"
          className={styles.gobackButton}
          onClick={onCancel}
        />
        <div
          className={styles.addOnTitle}
          {...setData(parentPhilter, "bundle-step-title")}
        >
          {bundleAddOnsStepTitle}
        </div>
      </div>

      <div className={styles.bundeleAddonStepInfo}>{bundleAddOnsStepText1}</div>

      <div className={styles.autoCompleteWrapper}>
        <SearchIcon
          fill={styles.searchIconColor}
          height={styles.searchIconDimension}
          stroke={styles.searchIconColor}
          width={styles.searchIconDimension}
        />
        <div ref={searchAddOnLayoutRef}>
          <FormInput
            autofocus
            onChange={onSearchForAddOns}
            onKeyDown={onEnterPress}
            placeholder={searchForYourRivals}
            required={false}
            type={InputType.TEXT}
            value={searchValue}
          />
          {!!searchValue && showSearchOverlay && (
            <div className={styles.searchAddOnsOverlay}>
              {filteredAddons && filteredAddons.length ? (
                filteredAddons.map((addon, index) => (
                  <CustomButton
                    ariaLabel="add-addon-to-bundle"
                    className={classNames(styles.searchAddOnOption, {
                      [styles.selectableOption]:
                        filteredAddons && filteredAddons.length,
                      [styles.disabledOption]:
                        bundle && bundle.length >= MAX_BUNDLE_SIZE
                    })}
                    key={index}
                    onClick={(): void => onAddAddOnToBundle(addon)}
                  >
                    <>
                      <div>
                        <div data-test="addon-on-suggestion-contentSiteName">
                          {addon.contentSiteName}
                        </div>
                        <div>{addon.siteName}</div>
                      </div>
                      <RivalsButton
                        buttonText={addButton}
                        disabled={bundle && bundle.length >= MAX_BUNDLE_SIZE}
                        size="small"
                        {...setData(parentPhilter, "bundle-step-add-button")}
                      />
                    </>
                  </CustomButton>
                ))
              ) : (
                <div className={styles.searchAddOnOption}>
                  <span>{noResults}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Row gutter={16}>
        {bundle &&
          _.range(MAX_BUNDLE_SIZE).map((i, index) => (
            <Col className={styles.addOnColumn} key={index} sm={8} xs={24}>
              <div className={styles.addOnNumber}>{i + 1}</div>
              <div
                className={classNames(
                  styles.addOnCard,
                  { [styles.selectedAddOnCard]: !!bundle[i] },
                  { [styles.unSelectedAddOnCard]: !bundle[i] }
                )}
              >
                {bundle[i] ? (
                  <>
                    <div
                      className={styles.addOnCardHeader}
                      style={{
                        backgroundColor: bundle[i].siteColor,
                        backgroundImage: `url(${bundle[i].siteLogoUrl})`
                      }}
                    ></div>
                    <div className={styles.removeButtonWrapper}>
                      <RivalsButton
                        buttonText={removeButton}
                        className={styles.removeButton}
                        inverted
                        onClick={(): void =>
                          onRemoveAddOnFromBundle(bundle[i].id)
                        }
                        size="small"
                        {...setData(parentPhilter, "bundle-step-remove-button")}
                      />
                    </div>
                  </>
                ) : (
                  searchAnotherSchool
                )}
              </div>
            </Col>
          ))}
      </Row>

      <div className={styles.buttons}>
        <RivalsButton
          buttonText={nextButton}
          className={styles.submitButton}
          disabled={bundle && bundle.length < MAX_BUNDLE_SIZE}
          onClick={onMoveToNextStep}
          size="small"
          {...setData(parentPhilter, "bundle-step-next-button")}
        />
        <RivalsButton
          buttonText={cancelButton}
          className={styles.cancelButton}
          inverted
          onClick={onCancel}
          size="small"
          {...setData(parentPhilter, "bundle-step-cancel-button")}
        />
      </div>
    </div>
  );
};

export default BundleAddOnsStep;
